import React, { ForwardRefExoticComponent, SVGProps } from 'react'

import { Check } from '@factorialco/factorial-one-react/icons/app'
import { FormsElementTypeEnum } from 'generated/resources/datatable.graphql'

import ChoiceElementEditor from 'modules/forms/components/editor/ChoiceElementEditor/ChoiceElementEditor'

import { OptionElementQuestion } from '../../components/QuestionComponent/OptionElementQuestion'
import { Element } from '../Element'
import { Answer, elementParam } from '../types'

/**
 * MultipleChoiceElement class for elements with multiple selectable options.
 * Extends the base Element class.
 */
export class MultipleChoiceElement extends Element {
  /**
   * Get the type of the element
   */
  getType(): FormsElementTypeEnum {
    return 'multiselect'
  }

  /**
   * Get the icon for the element
   */
  getIcon(): ForwardRefExoticComponent<SVGProps<SVGSVGElement>> {
    return Check
  }

  /**
   * Validate the element's configuration
   */
  validate(): boolean {
    // Basic validation: label should not be empty
    if (!this.label.trim()) {
      return false
    }

    // There should be at least two options
    const options = this.options
    if (options.length < 2) {
      return false
    }

    // Each option should have a non-empty label
    for (const option of options) {
      if (!option.label.trim()) {
        return false
      }
    }

    // If minChoices is specified, it should be a positive number
    const minChoices = this.minChoices
    if (minChoices !== undefined && minChoices < 0) {
      return false
    }

    // If maxChoices is specified, it should be greater than or equal to minChoices
    const maxChoices = this.maxChoices
    if (maxChoices !== undefined && minChoices !== undefined && maxChoices < minChoices) {
      return false
    }

    // If maxChoices is specified, it should not exceed the number of options
    if (maxChoices !== undefined && maxChoices > options.length) {
      return false
    }

    return true
  }

  /**
   * Get a React component for editing this element
   *
   * @param _onChange - Callback function to handle changes to the element (unused)

   * @returns A React component for editing this element
   */
  getEditorComponent(onChange: (element: Element) => void): React.ReactNode {
    return <ChoiceElementEditor element={this} onChange={onChange} />
  }

  getPreviewComponent(): React.ReactNode {
    return <OptionElementQuestion element={this} isSingleChoice={false} />
  }

  getQuestionComponent(onChange: (answer: Answer) => void): React.ReactNode {
    return <OptionElementQuestion element={this} isSingleChoice={false} onChange={onChange} />
  }

  // getDefaultParams(): Record<string, any> {
  //   return {
  //     maxChoices: 999,
  //     minChoices: 1,
  //     randomizeOptions: false,
  //     options: [],
  //   }
  // }

  /**
   * Get the maximum number of options that can be selected
   */
  get maxChoices(): number | undefined {
    return this.getParam<number>('maxChoices')
  }

  /**
   * Set the maximum number of options that can be selected
   */
  set maxChoices(value: number | undefined) {
    if (value !== undefined) {
      this.setParam('maxChoices', value)
    }
  }

  /**
   * Get the minimum number of options that must be selected
   */
  get minChoices(): number | undefined {
    return this.getParam<number>('minChoices')
  }

  /**
   * Set the minimum number of options that must be selected
   */
  set minChoices(value: number | undefined) {
    if (value !== undefined) {
      this.setParam('minChoices', value)
    }
  }

  /**
   * Get the available options for the element
   */
  // get options(): Option[] {
  //   return this.getParam<Option[]>('options') ?? []
  // }

  /**
   * Set the available options for the element
   */
  // set options(value: Option[]) {
  //   this.setParam('options', value)
  // }

  /**
   * Add an option to the element
   *
   * @param option - The option to add
   */
  // addOption(option: Option): void {
  //   const options = [...this.options]
  //   options.push(option)
  //   this.options = options
  // }

  /**
   * Remove an option from the element
   *
   * @param optionId - The ID of the option to remove
   */
  // removeOption(optionId: string): void {
  //   this.options = this.options.filter((option) => option.id !== optionId)
  // }

  /**
   * Update an option in the element
   *
   * @param optionId - The ID of the option to update
   * @param newLabel - The new label for the option
   */
  // updateOption(optionId: string, newLabel: string): void {
  //   const options = [...this.options]
  //   const optionIndex = options.findIndex((option) => option.id === optionId)
  //   if (optionIndex !== -1) {
  //     options[optionIndex] = { id: optionId, label: newLabel }
  //     this.options = options
  //   }
  // }

  /**
   * Get whether to display options in a random order
   */
  get randomizeOptions(): boolean {
    return this.getParam<boolean>('randomizeOptions') ?? false
  }

  /**
   * Set whether to display options in a random order
   */
  set randomizeOptions(value: boolean) {
    this.setParam('randomizeOptions', value)
  }

  configureParams(): elementParam[] {
    return [Element.booleanParam('randomizeOptions', 'Randomize')]
  }
}
